import type { View } from './project';
import { toScreen } from './project';

/*
 * THE NAMES THEMSELVES — where each one lands, how bright it is, and how big a target it makes.
 *
 * Ported from `reference/universe-v8/engine.js` — `drawStatic` lines 1566–1587. The PAINTING half
 * of that block (the halo, the five shapes, the corpus ring, 1588–1638) is the next unit; this is
 * the arithmetic that decides what there is to paint, and it is the half other layers depend on.
 *
 * `shown` IS THE SEAM. The node labels read it to know what is on screen and how big, and the
 * picker reads it to know what a tap landed on. Computing it once, in the order the discs are
 * drawn, is what keeps those three from disagreeing about where a name is — a class of bug that
 * cannot be tested for from any one of them.
 *
 * FOUR THINGS DECIDE HOW BRIGHT A NAME IS, and they multiply:
 *
 *   the zoom       nothing at all when pulled right back, so an unreadable field is not drawn
 *   the filter     a name the reader switched off drops to a tenth, rather than vanishing
 *   its weight     a minor name needs the reader closer before it is fully lit
 *   the fog        walked, glimpsed, or unvisited — what the reader has actually been to
 *
 * The filter DIMS rather than hides, which is a real decision: a name that disappeared would leave
 * a hole the reader could not ask about, and the map's whole claim is that position means something.
 */

/** One name, as this layer needs it. */
export interface MapNode {
  /** Its index into the fog sets. Theirs is a number and stays one. */
  id: number;
  /** What it is called. The only thing on the map that is words rather than position. */
  name: string;
  x: number;
  y: number;
  /** Its radius in WORLD units. */
  r: number;
  /** Its weight — how much is written about it. */
  w: number;
  /** Which scriptures name it, one character each. */
  cs: string;
  /** The one that names it most, which colours the disc. */
  dom: string;
  kind: string;
  /** Whether the roster is sure this is the figure it says. Unverified names are drawn fainter. */
  verified: boolean;
  /** How many readers are here. Zero when the presence plane is not connected. */
  pop: number;
}

/**
 * THE WEIGHT-TO-ZOOM CURVE — how close the reader must be before a name is fully lit.
 *
 * `(base + (topWeight − w) · perWeight) · scale` is a ZOOM, and every one of the four numbers is
 * measured against their weights and their world. Theirs works out to a heavy name being fully lit
 * by z=0.043 and the lightest not until z=0.18, which is the whole reason the field resolves from
 * the middle outward as the reader comes in rather than appearing all at once.
 */
export interface NeedCurve {
  base: number;
  perWeight: number;
  /** The heaviest weight in the roster — where the curve bottoms out. */
  topWeight: number;
  scale: number;
}

/** The world dimensions this layer measures against. All arguments, none with a default. */
export interface NodeWorld {
  /**
   * How deep the field is, for the depth cue, in world units.
   *
   * Their 2,600 against a rim of 12,320. A name at that depth is at the full cue and anything past
   * it is clamped, so on a bigger world a copied 2,600 would put every name at the extreme.
   */
  depthHalf: number;
  need: NeedCurve;
  /**
   * How many TRADITIONS make a name SHARED, and drawn as a hollow with a pale rim rather than in
   * one text's colour.
   *
   * Theirs is 3 of 4 CORPORA — three quarters of its library. Ours is every tradition we carry,
   * which is a deliberate departure: a figure in the Hebrew Bible, the Mishnah and the Talmud is
   * three texts and one tradition, and the hollow is meant to say "the traditions meet here". See
   * `traditionsNaming`, which is the quantity this is a threshold on and which was NOT what the
   * three call sites compared it against until 2026-08-21.
   */
  sharedAtLeast: number;
  /** Which traditions each corpus KEY belongs to, so `traditionsNaming` can union them. */
  traditionsOfKey: Readonly<Record<string, readonly string[]>>;
  palette: Readonly<Record<string, string>>;
  squash: number;
}

/**
 * The zooms the layer fades in over — arguments, because a zoom is pixels per world unit.
 *
 * The eighth and ninth absolute zooms this port has lifted out. Their `minZoom` comment says the
 * floor must never fall below this ramp, or the reader is left looking at a blank field.
 */
export interface NodeZooms {
  /** Below this no name is drawn at all. */
  from: number;
  /** And over this much more zoom they come fully in. */
  span: number;
}

/**
 * THEIRS: engine.js:1566 reads `Math.min(1,Math.max(0,(z-0.012)/0.02))` — the resident names
 * appear at 0.012 and are fully in a fiftieth of a zoom later.
 *
 * Exported so the wiring and the test read one copy. They did not: this is the fifth threshold in
 * this port found invented, and the span was TEN TIMES theirs.
 */
export const THEIR_NODE_ZOOMS: NodeZooms = { from: 0.012, span: 0.02 };

/** What the reader has been to. */
export interface NodeFog {
  /** Names they have opened. */
  visited: ReadonlySet<number>;
  /** Names they have passed near. */
  seen: ReadonlySet<number>;
  /**
   * Whether the whole field is being shown regardless, and how far the fog has closed back in.
   *
   * The arrival flies over everything before the dark closes around where the reader landed, so
   * during that beat every name is lit and `fogMix` walks it back to normal.
   */
  revealAll?: boolean;
  fogMix?: number;
}

/** A name as it was drawn: where, how big, how bright, and how big a target it makes. */
export interface ShownNode {
  node: MapNode;
  sx: number;
  sy: number;
  /** Its radius on screen. */
  r: number;
  /** Its alpha. */
  a: number;
  /** Whether the reader's filter lets it through. */
  vis: boolean;
  /** How much of the fog it is under. */
  fog: number;
  /**
   * The radius a tap must land inside — the corpus ring's outer edge, not the disc's.
   *
   * Bigger than the disc whenever a name carries a ring, because the ring is part of the thing the
   * reader is pointing at. A picker measuring the disc alone would refuse taps that visibly hit.
   */
  hit: number;
}

/** Below this the layer is not worth a single draw call. */
export const NODE_MIN_ALPHA = 0.02;

/** Below this much spin there is no depth cue and the sort is skipped. */
const DEPTH_CUE_MIN = 0.02;

/** How much nearer names brighten and further ones dim, at the full cue. */
const DEPTH_LIFT = 0.16;

/** The smallest and largest a name is drawn on screen. */
const RADIUS_MIN = 1.1;
const RADIUS_MAX = 46;

/** How far past the screen edge a name is still drawn. */
const CULL_MARGIN = 30;

/** How lit a name is by what the reader has done: walked, glimpsed, or neither. */
const FOG_WALKED = 1;
const FOG_SEEN = 0.42;
const FOG_UNSEEN = 0.1;

/** What the filter and the roster's own doubt do to a name's alpha. */
const FILTERED_OUT = 0.1;
const UNVERIFIED = 0.55;

/**
 * The floor under the weight ramp, so even the lightest name is faintly there.
 *
 * UNREACHABLE ON THEIR OWN CURVE, and worth recording rather than tidying. It binds only when
 * `z < 0.05 · need`, and their heaviest need is 0.18 — so it would take a zoom below 0.009, while
 * the whole layer is already switched off below 0.0124. On a roster whose weights run to 40 rather
 * than 10 the need reaches 0.59 and the floor binds properly, which is why it is kept: it is the
 * guard that stops a wider weight scale blanking its own lightest names. Found by mutating it and
 * watching the tests pass, the same way the stars' widest dot turned out never to be drawn.
 */
const NEED_FLOOR = 0.05;

/** The ring is drawn only once a name is big enough to carry one. */
const RING_MIN_R = 3.2;

/** How far outside the disc the ring sits, and how thick it is. */
const RING_GAP_MIN = 2.6;
const RING_GAP_SHARE = 0.42;
const RING_WIDTH_MIN = 1.2;
const RING_WIDTH_MAX = 3.2;
const RING_WIDTH_SHARE = 0.2;

/** How bright the names are at this zoom, before anything else is applied. */
export function nodeAlpha(zoom: number, zooms: NodeZooms): number {
  return Math.min(1, Math.max(0, (zoom - zooms.from) / zooms.span));
}

/**
 * THEIRS: engine.js:1580 reads (0.17+(10-n.w)*0.055)*0.25 — a name of weight 10 is fully lit at
 * 0.0425, and every step down the weight scale costs another 0.055 of zoom before it is.
 *
 * `topWeight` is 10 because that is the scale THEIR weights run on. Ours run higher, so `perWeight`
 * has to be read against our own top — the same arithmetic `labelWeightsFor` does for the label
 * ladder, and for the same reason: a weight means nothing without the scale it is counted on.
 */
export const THEIR_NEED = { base: 0.17, perWeight: 0.055, topWeight: 10, scale: 0.25 } as const;

/** The zoom at which a name of this weight is fully lit. */
export function needZoom(weight: number, need: NeedCurve): number {
  return (need.base + (need.topWeight - weight) * need.perWeight) * need.scale;
}

/** How much of a name's brightness its weight has earned at this zoom. */
export function weightLit(zoom: number, weight: number, need: NeedCurve): number {
  return Math.min(1, Math.max(NEED_FLOOR, zoom / needZoom(weight, need)));
}

/** How big a name is on screen. */
export function nodeRadius(worldRadius: number, zoom: number): number {
  return Math.max(RADIUS_MIN, Math.min(RADIUS_MAX, worldRadius * zoom));
}

/** Which scriptures name it, of the ones we draw. */
export function namedBy(node: MapNode, palette: Readonly<Record<string, string>>): string[] {
  return [...node.cs].filter(k => palette[k] !== undefined);
}

/**
 * How far out a tap still counts as hitting this name.
 *
 * The ring's outer edge when it carries one, and the disc otherwise. Derived here rather than in
 * the picker so the two cannot drift — the reader's finger and the paint must agree.
 */
export function ringRadius(r: number, corpora: number): number {
  const ring = ringGeometry(r, corpora);
  return ring === null ? r : ring.rr + ring.lw * 0.5;
}

/**
 * Where the corpus ring sits and how thick it is, or null when a name does not carry one.
 *
 * Derived once and used twice — by `ringRadius` for the hit target and by the painting for the arcs
 * themselves. Two copies of this arithmetic is exactly how a ring comes to be drawn somewhere the
 * picker is not looking.
 */
export function ringGeometry(r: number, corpora: number): { rr: number; lw: number } | null {
  if (corpora <= 1 || r < RING_MIN_R) return null;
  return {
    rr: r + Math.max(RING_GAP_MIN, r * RING_GAP_SHARE),
    lw: Math.max(RING_WIDTH_MIN, Math.min(RING_WIDTH_MAX, r * RING_WIDTH_SHARE)),
  };
}

/**
 * How far into the field a name sits, from −1 (nearest) to 1 (furthest).
 *
 * Measured along the spin's own axis, so "into the field" means away from the reader once the world
 * has tilted. Zero when the world is flat on, because then there is no depth to cue.
 */
export function depthOf(node: MapNode, view: View, world: NodeWorld, spinFraction: number): number {
  if (spinFraction <= DEPTH_CUE_MIN) return 0;
  const along = node.x * view.vs + (node.y / (world.squash || 1)) * view.vc;
  return Math.max(-1, Math.min(1, along / world.depthHalf));
}

/**
 * Back to front, so a nearer name paints over a further one.
 *
 * Only when the world is tilted enough for it to matter — a flat field has no front and back, and
 * sorting ten thousand names for nothing is exactly the kind of per-frame cost this engine avoids.
 * The roster's own order is returned unchanged in that case, NOT a copy.
 */
export function sortByDepth(nodes: readonly MapNode[], view: View, squash: number, spinFraction: number): readonly MapNode[] {
  if (spinFraction <= DEPTH_CUE_MIN) return nodes;
  const sq = squash || 1;
  const along = (n: MapNode): number => n.x * view.vs + (n.y / sq) * view.vc;
  return [...nodes].sort((p, q) => along(p) - along(q));
}

/**
 * Everything on screen, in the order it is painted.
 *
 * `visible` is the reader's filter, passed in rather than rebuilt — `web.ts` owns it, because the
 * same filter decides which ties are drawn.
 */
export function shownNodes(
  view: View,
  spinFraction: number,
  nodes: readonly MapNode[],
  world: NodeWorld,
  zooms: NodeZooms,
  fog: NodeFog,
  visible: (node: MapNode) => boolean,
): ShownNode[] {
  const z = view.cam.z;
  const alpha = nodeAlpha(z, zooms);
  const shown: ShownNode[] = [];
  if (alpha <= NODE_MIN_ALPHA) return shown;
  const W = view.view.width;
  const H = view.view.height;
  const cue = spinFraction > DEPTH_CUE_MIN ? spinFraction : 0;

  for (const node of sortByDepth(nodes, view, world.squash, spinFraction)) {
    const p = toScreen(view, node.x, node.y);
    const depth = depthOf(node, view, world, spinFraction);
    /* Nearer names a little brighter, further ones a little dimmer — the only thing that separates
       the front of the field from the back once everything is the same size. */
    const lift = 1 + depth * DEPTH_LIFT * cue;
    const r = nodeRadius(node.r, z);
    if (p.x < -r - CULL_MARGIN || p.x > W + r + CULL_MARGIN) continue;
    if (p.y < -r - CULL_MARGIN || p.y > H + r + CULL_MARGIN) continue;
    const vis = visible(node);
    let lit = (fog.visited.has(node.id) ? FOG_WALKED : fog.seen.has(node.id) ? FOG_SEEN : FOG_UNSEEN) * lift;
    /* During the arrival the whole field is lit, and `fogMix` walks it back as the dark closes. */
    if (fog.revealAll === true) lit = 1 + (lit - 1) * (fog.fogMix ?? 0);
    const a = alpha
      * (vis ? (node.verified ? 1 : UNVERIFIED) : FILTERED_OUT)
      * weightLit(z, node.w, world.need)
      * lit;
    const corpora = namedBy(node, world.palette).length;
    shown.push({ node, sx: p.x, sy: p.y, r, a, vis, fog: lit, hit: ringRadius(r, corpora) });
  }
  return shown;
}

/**
 * HOW MANY TRADITIONS NAME A FIGURE — the quantity `sharedAtLeast` is a threshold ON.
 *
 * `sharedAtLeast` is `traditions.size`: Judaism, Christianity and Islam, three. Every reader of it
 * compared it against a count of CORPORA, of which we carry five, and the field's own doc said "how
 * many traditions must name a figure" — so anybody checking one against the other read agreement.
 * It is the shape `CLAUDE.md` records for the floor and for `revealAll`: two names for one
 * quantity, and nothing goes red on the day it is wrong, because every test was handed one number
 * for both. `our-scene.test.ts` even asserts that a fourth JEWISH text must not move the number,
 * which is exactly the case the comparison got wrong.
 *
 * What it cost on the bundle today: 109 names drawn as apertures — dark core, no colour — where the
 * rule as written gives 42. Sixty-seven figures lost their scripture's colour, and a figure in the
 * Hebrew Bible, the Mishnah and the Talmud is one tradition three times over, not a shared one.
 *
 * A corpus may belong to more than one tradition, so the traditions are UNIONED rather than counted
 * one per corpus.
 */
export function traditionsNaming(
  keys: readonly string[],
  traditionsOfKey: Readonly<Record<string, readonly string[]>>,
): number {
  const seen = new Set<string>();
  for (const key of keys) for (const tradition of traditionsOfKey[key] ?? []) seen.add(tradition);
  return seen.size;
}

/**
 * THE THREE FACTS THAT DECIDE WHETHER A NAME IS SHARED, and nothing else.
 *
 * `NodeWorld` and `NodePaint` both carry them, and taking the narrow shape rather than either whole
 * one is what lets the LAYER THAT DRAWS and the layer that measures run the same rule. They did not:
 * `nodes-draw.ts` kept a private copy of `namedBy` — commented "Duplicated from `nodes.ts` on
 * purpose: cheap" — and inlined the comparison beside it, so `isShared` was exported, tested six
 * ways, and called by nothing but its own test.
 *
 * Which is this project's own recorded shape twice over: a rule ported, covered and unreachable, and
 * a second copy nothing keeps in step. The duplicate WAS in step, as it happens — both counted
 * traditions. That is the reason to merge them now rather than after they disagree, because the day
 * they disagree the tested one is the one that is not on screen.
 */
export interface SharedRule {
  readonly palette: Readonly<Record<string, string>>;
  readonly traditionsOfKey: Readonly<Record<string, readonly string[]>>;
  readonly sharedAtLeast: number;
}

/** Whether a name is claimed by enough TRADITIONS to be drawn as shared rather than as one text's. */
export function isShared(node: MapNode, rule: SharedRule): boolean {
  return traditionsNaming(namedBy(node, rule.palette), rule.traditionsOfKey) >= rule.sharedAtLeast;
}
